import React, { useMemo, useState } from 'react'
import WithHeader from '../components/WithHeader'
import { ActivityIndicator, Platform, View } from 'react-native'
import { useSelector, useDispatch } from 'react-redux'
import _ from 'lodash'

import { ListItemLinkButton } from '../components/ListItemLinkButton'
import { ListItem } from 'react-native-elements'

import webVersion from '../version.web.json'
import colors from '../styles/colors'
import { requestGlobalToken, selectTenant } from '../store/user/actions'
import { useTenantOrGlobalAdmin } from '../hooks/useTenantOrGlobalAdmin'
import { roles } from '../components/fields/MultipleRolePicker'
import { showMessage } from 'react-native-flash-message'
import MSFESButton from '../components/MSFESButton'
import spacing from '../styles/spacing'
import api from '../data/api'
import MSFESInput from '../components/MSFESInput'
import {
  usePermissionsV2Checker,
  useV1Permission
} from '../hooks/usePermissionsV2Checker'

export default function SettingsIndexScreen({ navigation }) {
  const pageTitle = 'Settings'

  const { current_user } = useSelector((state) => {
    return { current_user: _.get(state, 'users.user') }
  })
  const {
    isGlobalAdmin,
    isTenantAdmin,
    actingRole,
    tenant
  } = useTenantOrGlobalAdmin()

  const permissions = usePermissionsV2Checker()

  const hasAdminToolsPermission = useV1Permission
    ? _.get(
        current_user.effective_permissions,
        'view||name:admin_modules',
        false
      )
    : permissions.hasPermission(
        permissions.view,
        null,
        permissions.systemPermissions.adminModules
      ) ||
      isGlobalAdmin ||
      isTenantAdmin

  const hasPermissionModdingPermission = useV1Permission
    ? _.get(
        current_user.effective_permissions,
        'update||name:tenant-level-permissions',
        false
      ) ||
      _.get(
        current_user.effective_permissions,
        'update||name:privileged-permissions',
        false
      )
    : permissions.hasPermission(
        permissions.update,
        null,
        permissions.systemPermissions.privilegedPermissions
      ) ||
      permissions.hasPermission(
        permissions.update,
        null,
        permissions.systemPermissions.tenantLevelPermissions
      )

  const hasCollectionsPermission = useV1Permission
    ? _.get(
        current_user.effective_permissions,
        'update||name:collections',
        false
      )
    : permissions.hasPermission(
        permissions.update,
        null,
        permissions.systemPermissions.collections
      )

  const environment = useSelector((state) => {
    return state.app.environment
  })

  let versionLabel = useMemo(() => {
    let versionLabel = environment + ''

    versionLabel += '/' + webVersion.hash

    return versionLabel
  }, [environment, webVersion])

  const [isClearingCache, setIsClearingCache] = useState(false)

  const dispatch = useDispatch()

  const isElevatedGlobalUser = useMemo(() => {
    return (
      isGlobalAdmin &&
      ['rhysw@hey.com', 'dai@msfes.net', 'christina@msfes.net'].includes(
        current_user.email
      )
    )
  }, [isGlobalAdmin, current_user])

  const [isLoadingToken, setIsLoadingToken] = useState(false)
  return (
    <WithHeader title={pageTitle} navigation={navigation}>
      <View key="view-module-wrapper">
        <View>
          {hasAdminToolsPermission && (
            <ListItemLinkButton
              title={'Admin Tools'}
              to={`/settings/admin-tools/index`}
            />
          )}
          {hasPermissionModdingPermission && (
            <ListItemLinkButton
              title={'Permissions Editor (Deprecated)'}
              to={`/settings/admin-tools/permissions`}
            />
          )}
          {hasCollectionsPermission && (
            <ListItemLinkButton
              title={'Collections'}
              to={`/settings/collections`}
            />
          )}
          <ListItemLinkButton
            title={'Reports'}
            to={`/settings/reports/index`}
          />

          <ListItem>
            <ListItem.Title style={{ color: colors.labelGray }}>
              About: {versionLabel} API: {current_user.api_version}
            </ListItem.Title>
          </ListItem>

          <MSFESButton
            title="Update Ember"
            icon={{ name: 'refresh' }}
            containerStyle={{
              padding: spacing.m1,
              flex: 1,
              minHeight: 64
            }}
            disabled={isClearingCache}
            onPress={() => {
              setIsClearingCache(true)

              const onSuccess = () => {
                showMessage({
                  message: 'Cache cleared.',
                  type: 'success'
                })
                if (Platform.OS === 'web') {
                  window.location.reload()
                }
              }
              const onFinally = () => {
                setIsClearingCache(false)
              }

              dispatch({
                type: 'entity/reset'
              })

              if (isGlobalAdmin) {
                dispatch(requestGlobalToken())
                  .then(onSuccess)
                  .finally(onFinally)
              } else {
                const role = roles.find((r) => r.key === actingRole)
                dispatch(selectTenant({ tenant, role }))
                  .then(onSuccess)
                  .finally(onFinally)
              }
            }}
          />

          {isElevatedGlobalUser && (
            <View style={{ padding: spacing.m1, flexDirection: 'row' }}>
              {isLoadingToken && <ActivityIndicator />}
              <View></View>
              <View style={{ flex: 1 }}>
                <MSFESInput
                  placeholder="Enter email"
                  returnKeyType="done"
                  onBlur={(e) => {
                    const email =
                      Platform.OS === 'web'
                        ? e.target.value
                        : e.nativeEvent.text

                    setIsLoadingToken(true)
                    api
                      .post('admin/logins/get-token', { email })
                      .then((response) => {
                        dispatch({
                          type: 'users/signin-success',
                          ...response
                        })
                      })
                      .catch((e) => {
                        showMessage({ message: e.message, type: 'danger' })
                      })
                      .finally(() => {
                        setIsLoadingToken(false)
                      })
                  }}
                />
              </View>
            </View>
          )}
        </View>
      </View>
    </WithHeader>
  )
}
